import React, { useState, useEffect } from 'react';
import {
  Box,
  CardHeader,
  Avatar,
  IconButton,
  Typography,
  Button,
  Tooltip,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AddCircle, RemoveCircle } from '@mui/icons-material';

import CodeEditorV2 from './CodeEditorV2';
import SelectedTileHeader from './SelectedTileHeader';

import { setLoading } from '../reducers/loadingReducer';
import { addMessage, selectChatContext } from '../reducers/messagesReducer';
import { showToast } from '../reducers/uiReducer';
import { sendMessage } from '../services/apiService';

import {
  setIsActiveChat,
  setSelectedTileId,
  addPromptItem,
  removePromptItem,
  updatePromptItem,
  clearSelectedPromptItem,
  selectedPromptItem,
  setSelectedPromptItem,
} from '../reducers/uiReducer';

const DocumentSelection = ({
  show,
  onClose,
  icon,
  title,
  setPromptCallBack,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const chatContext = useSelector(selectChatContext);
  const selectedItem = useSelector(selectedPromptItem);
  const loading = useSelector((state) => state.loading);

  const [showCodePromptItem, setShowCodePromptItem] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [code, setCode] = useState(null);
  const [language, setLanguage] = useState('javascript');

  useEffect(() => {
    if (selectedItem && selectedItem.type === 'code_assistant') {
      setShowCodePromptItem(true);
    } else {
      setShowCodePromptItem(false); // Optionally reset if the condition is not met
    }
  }, [selectedItem]);

  const handleClose = () => {
    onClose();
    dispatch(setSelectedPromptItem(null));
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleRunPrompt = async (prompt) => {
    const fullPrompt = `${prompt} \n\n${code}`;
    setAnchorEl(null);

    dispatch(setSelectedTileId(null));
    dispatch(setIsActiveChat(true));
    dispatch(setLoading(true));

    const newUserMessage = {
      role: 'user',
      componentType: 'code',
      content: [{ type: 'text', text: fullPrompt }],
    };

    const updatedChatContext = [...chatContext, newUserMessage];
    dispatch(addMessage(newUserMessage));

    try {
      const response = await sendMessage(updatedChatContext);
      if (response?.choices?.[0]?.message) {
        dispatch(
          addMessage({
            role: 'assistant',
            content: [
              { type: 'text', text: response.choices[0].message.content },
            ],
          }),
        );
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      dispatch(
        addMessage({
          role: 'error',
          content: [{ type: 'text', text: 'Service Unavailable.' }],
        }),
      );
      dispatch(
        showToast({ message: 'Failed to send message', severity: 'error' }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddPrompt = (code) => {
    const id = `Code_Snippet_${Math.floor(10000 + Math.random() * 90000)}`;
    const newPromptItem = {
      id,
      type: 'code_assistant',
      showIcon: true,
      code,
      language,
    };
    dispatch(setSelectedPromptItem(newPromptItem));
    dispatch(addPromptItem(newPromptItem));
  };

  const handleRemovePrompt = (id) => {
    dispatch(clearSelectedPromptItem(null));
    dispatch(removePromptItem(id));
  };

  const handleUpdatePrompt = (id) => {
    dispatch(
      updatePromptItem({
        id,
        updates: {
          type: 'code_assistant',
          showIcon: true,
          code,
          language,
        },
      }),
    );
    dispatch(
      setSelectedPromptItem({
        id,
        type: 'code_assistant',
        showIcon: true,
        code,
        language,
      }),
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 400,
        animation: 'fadeIn 0.4s ease-in forwards',
        transition: 'opacity 0.2s ease',
        borderRadius: 2,
        boxShadow: 3,
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #1e1e1e, #2e2e2e)'
            : 'linear-gradient(180deg, #fff, #e4f2fb, #f2f2f2)',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <CardHeader
        sx={{ pb: 0 }}
        action={
          <Tooltip title="Cancel">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        }
        title={
          <SelectedTileHeader
            icon={icon}
            title={title}
            setShowSelectedItem={setShowCodePromptItem}
            selectedItem={selectedItem?.id}
            setSelectedItem={(item) => dispatch(setSelectedPromptItem(item))}
          />
        }
      />

      <CardContent sx={{ p: 0, height: '100%' }}>
        <CodeEditorV2
          key={selectedItem ? selectedItem.id : 'default'}
          onChange={(updatedCode, updatedLang) => {
            setCode(updatedCode);
            setLanguage(updatedLang);
          }}
          initialCode={selectedItem?.code || ''}
          language={selectedItem?.language || language}
        />
      </CardContent>

      <Box sx={{ p: 2 }}>
        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Button
              variant="outlined"
              onClick={handleMenuOpen}
              endIcon={<KeyboardArrowDownIcon />}
            >
              Quick Prompts
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleMenuClose}
            >
              {[
                'Explain this code.',
                'Review this code and suggest changes.',
                'Check for errors in this code.',
              ].map((prompt, idx) => (
                <MenuItem key={idx} onClick={() => handleRunPrompt(prompt)}>
                  {prompt}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box>
            <Tooltip
              title={
                selectedItem ? 'Update code in prompt' : 'Add code to prompt'
              }
            >
              <Button
                variant="contained"
                startIcon={<AddCircle />}
                onClick={() =>
                  selectedItem
                    ? handleUpdatePrompt(selectedItem.id)
                    : handleAddPrompt(code)
                }
                sx={{ mr: selectedItem ? 1 : 0 }}
              >
                {selectedItem ? 'Update' : 'Add'}
              </Button>
            </Tooltip>
            {selectedItem && (
              <Tooltip title="Remove code from prompt">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemovePrompt(selectedItem.id)}
                  startIcon={<RemoveCircle />}
                >
                  Remove
                </Button>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      </Box>
    </Box>
  );
};

export default DocumentSelection;
